/**
 * Guide Lines
 *
 * Magnetic center guides for dragging: the object center snaps to the print
 * area center while two dashed system-layer lines mark the axes that snapped.
 */

import { Line, type Canvas } from 'fabric';

// Local names: isSystemLayer() matches the `__` prefix generically, so the
// shared SYSTEM_LAYER_NAMES (packages/shared) stays untouched.
export const GUIDE_V = '__guideV__';
export const GUIDE_H = '__guideH__';
export const SNAP_THRESHOLD_PX = 6;
/** Touch: the snap window in SCREEN pixels — a finger is no mouse pointer. */
export const TOUCH_SNAP_SCREEN_PX = 12;
const GUIDE_OVERHANG_PX = 12;

export interface Point2D {
  x: number;
  y: number;
}

export interface SnapResult {
  x: number | null;
  y: number | null;
}

interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Returns the snapped coordinate per axis, or null when outside the threshold. */
export function computeCenterSnap(
  objectCenter: Point2D,
  target: Point2D,
  threshold = SNAP_THRESHOLD_PX
): SnapResult {
  return {
    x: Math.abs(objectCenter.x - target.x) <= threshold ? target.x : null,
    y: Math.abs(objectCenter.y - target.y) <= threshold ? target.y : null,
  };
}

function findGuide(canvas: Canvas, name: string): Line | undefined {
  return canvas.getObjects().find((obj) => obj.name === name) as
    | Line
    | undefined;
}

function makeGuide(
  name: string,
  coords: [number, number, number, number],
  color: string
): Line {
  return new Line(coords, {
    name,
    stroke: color,
    strokeWidth: 1,
    strokeDashArray: [4, 4],
    selectable: false,
    evented: false,
    excludeFromExport: true,
    visible: false,
    objectCaching: false,
  });
}

/** Creates the two guide lines once per canvas and keeps them on the print area center. */
export function ensureGuideLines(
  canvas: Canvas,
  area: Area,
  color: string
): { v: Line; h: Line } {
  const cx = area.x + area.width / 2;
  const cy = area.y + area.height / 2;
  const vCoords: [number, number, number, number] = [
    cx,
    area.y - GUIDE_OVERHANG_PX,
    cx,
    area.y + area.height + GUIDE_OVERHANG_PX,
  ];
  const hCoords: [number, number, number, number] = [
    area.x - GUIDE_OVERHANG_PX,
    cy,
    area.x + area.width + GUIDE_OVERHANG_PX,
    cy,
  ];

  let v = findGuide(canvas, GUIDE_V);
  let h = findGuide(canvas, GUIDE_H);

  if (!v) {
    v = makeGuide(GUIDE_V, vCoords, color);
    canvas.add(v);
  } else {
    v.set({
      x1: vCoords[0],
      y1: vCoords[1],
      x2: vCoords[2],
      y2: vCoords[3],
      stroke: color,
    });
    // Line._set updates width/height/left/top but not aCoords.
    v.setCoords();
  }

  if (!h) {
    h = makeGuide(GUIDE_H, hCoords, color);
    canvas.add(h);
  } else {
    h.set({
      x1: hCoords[0],
      y1: hCoords[1],
      x2: hCoords[2],
      y2: hCoords[3],
      stroke: color,
    });
    // Line._set updates width/height/left/top but not aCoords.
    h.setCoords();
  }

  return { v, h };
}

/**
 * Shows/hides the guides.
 * Cheap on every object:moving because it returns early when nothing changes.
 */
export function showGuides(
  canvas: Canvas,
  area: Area,
  which: { v: boolean; h: boolean },
  color: string
): void {
  const existingV = findGuide(canvas, GUIDE_V);
  const existingH = findGuide(canvas, GUIDE_H);
  if (
    existingV &&
    existingH &&
    existingV.visible === which.v &&
    existingH.visible === which.h
  ) {
    return;
  }

  const { v, h } = ensureGuideLines(canvas, area, color);
  // User objects get pulled to the front by reinitializeLayers,
  // finalizeRestoredObjects and the layers panel, so re-raise on show.
  if (which.v && !v.visible) canvas.bringObjectToFront(v);
  if (which.h && !h.visible) canvas.bringObjectToFront(h);
  v.set({ visible: which.v });
  h.set({ visible: which.h });
  canvas.requestRenderAll();
}

export function hideGuides(canvas: Canvas): void {
  const v = findGuide(canvas, GUIDE_V);
  const h = findGuide(canvas, GUIDE_H);
  if (!v?.visible && !h?.visible) return;
  v?.set({ visible: false });
  h?.set({ visible: false });
  canvas.requestRenderAll();
}
